import deepEqual from "deep-equal";
import { boundarySections } from "@slootsantos/certain-forms/dist/forms/ce";

type Json = null | string | number | boolean | Json[] | { [key: string]: Json };
export interface BoundaryDraftValues {
  boundary: Record<string, Json>;
  additional_boundaries: { description: string; value: string }[];
}
export interface DraftIdentity {
  accountId: string;
  userId: string;
  projectId: string;
  form: "boundary";
}
export interface FormDraft {
  version: 1;
  identity: DraftIdentity;
  writer: string;
  revision: string;
  updatedAt: number;
  baseline: BoundaryDraftValues;
  pending?: BoundaryDraftValues;
  values: BoundaryDraftValues;
}

const fields = Array.from(
  new Set([
    ...boundarySections
      .filter((s) => s.name === "boundary")
      .flatMap((s) => s.fields.map((f) => f.name)),
    "detailed_description",
  ]),
);
export const emptyDescription = [
  { type: "paragraph", children: [{ text: "" }] },
];
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const json = (value: unknown): value is Json => {
  if (value === null || typeof value === "string" || typeof value === "boolean")
    return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(json);
  return (
    record(value) &&
    Object.getPrototypeOf(value) === Object.prototype &&
    Object.entries(value).every(
      ([key, v]) =>
        !["__proto__", "constructor", "prototype"].includes(key) && json(v),
    )
  );
};
const slateNode = (node: unknown): boolean =>
  record(node) &&
  ((typeof node.text === "string" && node.children === undefined) ||
    (typeof node.type === "string" &&
      Array.isArray(node.children) &&
      node.children.length > 0 &&
      node.children.every(slateNode)));
export const validDescription = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(slateNode) &&
  json(value);

// Only fields owned by this form belong in its draft or its update payload.
export const selectBoundaryValues = (
  values: Record<string, any>,
): BoundaryDraftValues => {
  const boundary: BoundaryDraftValues["boundary"] = {};
  for (const field of fields) {
    const value = values.boundary?.[field];
    boundary[field] =
      field === "detailed_description"
        ? JSON.parse(JSON.stringify(value || emptyDescription))
        : typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"
          ? value
          : "";
  }
  return {
    boundary,
    additional_boundaries: (
      values.additional_boundaries ??
      values.boundary?.additional_boundaries ??
      []
    ).map((item: { description?: string; value?: string }) => ({
      description: item.description ?? "",
      value: item.value ?? "",
    })),
  };
};
const validValues = (value: unknown): value is BoundaryDraftValues =>
  record(value) &&
  record(value.boundary) &&
  Object.keys(value).every(
    (key) => key === "boundary" || key === "additional_boundaries",
  ) &&
  Object.keys(value.boundary).length === fields.length &&
  fields.every((field) => {
    const v = (value.boundary as Record<string, unknown>)[field];
    return field === "detailed_description"
      ? validDescription(v)
      : typeof v === "string" ||
          typeof v === "boolean" ||
          (typeof v === "number" && Number.isFinite(v));
  }) &&
  Array.isArray(value.additional_boundaries) &&
  value.additional_boundaries.every(
    (item) =>
      record(item) &&
      Object.keys(item).length === 2 &&
      typeof item.description === "string" &&
      typeof item.value === "string",
  );

export const draftKey = (identity?: DraftIdentity): string | undefined =>
  identity &&
  identity.accountId &&
  identity.userId &&
  identity.projectId &&
  identity.form === "boundary"
    ? `certain.formDraft.v1.${[identity.accountId, identity.userId, identity.projectId, identity.form].map((part) => encodeURIComponent(part).replace(/\./g, "%2E")).join(".")}`
    : undefined;

export const readDraft = (identity?: DraftIdentity): FormDraft | undefined => {
  const key = draftKey(identity);
  if (!key) return;
  try {
    const draft = JSON.parse(localStorage.getItem(key) || "null");
    if (
      draft?.version === 1 &&
      deepEqual(draft.identity, identity) &&
      typeof draft.writer === "string" &&
      draft.writer &&
      typeof draft.revision === "string" &&
      draft.revision &&
      Number.isFinite(draft.updatedAt) &&
      (draft.pending === undefined || validValues(draft.pending)) &&
      validValues(draft.baseline) &&
      validValues(draft.values)
    )
      return draft;
  } catch {
    /* Storage can be denied or contain data from an older release. */
  }
};
export const writeDraft = (draft: FormDraft): boolean => {
  const key = draftKey(draft.identity);
  if (!key) return false;
  try {
    localStorage.setItem(key, JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
};
export const acknowledgeDraft = (draft?: FormDraft): void => {
  if (!draft) return;
  const current = readDraft(draft.identity);
  if (
    current?.writer !== draft.writer ||
    current?.revision !== draft.revision ||
    !deepEqual(current.values, draft.values)
  )
    return;
  try {
    localStorage.removeItem(draftKey(draft.identity)!);
  } catch {
    /* Keep recovery data if removal fails. */
  }
};

export const recoverDraft = (
  server: BoundaryDraftValues,
  identity?: DraftIdentity,
) => {
  const draft = readDraft(identity);
  if (!draft) return { values: server, conflict: false, writable: true };
  const values = JSON.parse(JSON.stringify(server)) as BoundaryDraftValues;
  let conflict = false;
  for (const field of fields) {
    const local = draft.values.boundary[field];
    const baseline = draft.baseline.boundary[field];
    const pending = draft.pending?.boundary[field];
    if (
      deepEqual(local, baseline) &&
      (pending === undefined || deepEqual(local, pending))
    )
      continue;
    if (
      deepEqual(server.boundary[field], baseline) ||
      (pending !== undefined && deepEqual(server.boundary[field], pending))
    )
      values.boundary[field] = local;
    else if (!deepEqual(server.boundary[field], local)) conflict = true;
  }
  if (
    !deepEqual(
      draft.values.additional_boundaries,
      draft.baseline.additional_boundaries,
    ) ||
    (draft.pending &&
      !deepEqual(
        draft.values.additional_boundaries,
        draft.pending.additional_boundaries,
      ))
  ) {
    if (
      deepEqual(
        server.additional_boundaries,
        draft.baseline.additional_boundaries,
      ) ||
      (draft.pending &&
        deepEqual(
          server.additional_boundaries,
          draft.pending.additional_boundaries,
        ))
    )
      values.additional_boundaries = draft.values.additional_boundaries;
    else if (
      !deepEqual(
        server.additional_boundaries,
        draft.values.additional_boundaries,
      )
    )
      conflict = true;
  }
  // Keep conflicting content recoverable before replacing the normal draft.
  let writable = true;
  if (conflict) {
    try {
      localStorage.setItem(
        `${draftKey(identity)}.conflict.${encodeURIComponent(draft.revision)}`,
        JSON.stringify(draft),
      );
    } catch {
      writable = false;
    }
  }
  if (!conflict && deepEqual(values, server)) acknowledgeDraft(draft);
  return { values, conflict, writable };
};
